import { AdjustedTime, getAdjustedTime } from "model/Match/AdjustedTime";
import { MatchTeams } from "model/Match/MatchTeams";
import { TimeAttackSet } from "model/Match/Set/TimeAttack/TimeAttackSet";
import {
  getTimeAttackAverageScore,
  TimeAttackScoringAverage,
} from "model/Match/Set/TimeAttack/Track/Scoring/TimeAttackScoringAverage";
import { getRosterId, getRosterDriverIds } from "model/Roster/Roster";
import { nonUndefined } from "Utility";

export type SmsTimeAttackRecordTrack = {
  rank: number | undefined;
  score: number | undefined;
  sortedTimes: (AdjustedTime | undefined)[];
};

export type SmsTimeAttackRecord = {
  teamOrDriverId: string;
  rosterId: string;
  position: number;
  tracks: SmsTimeAttackRecordTrack[];
  totalScore: number | undefined;
};

export type SmsTimeAttackRecordWithDisplay = {
  record: SmsTimeAttackRecord;
  orgId: string | undefined;
  displayName: string | undefined;
  orgName: string | undefined;
};

const createTimeAttackRecord = (
  teamOrDriverId: string,
  rosterId: string,
  numberOfScores: number
): SmsTimeAttackRecord => {
  return {
    teamOrDriverId: teamOrDriverId,
    rosterId,
    position: 0,
    tracks: Array(numberOfScores).fill({
      score: undefined,
      sortedTimes: [],
      rank: undefined,
    }),
    totalScore: undefined,
  };
};

export const calculateTeamSmsTimeAttackRecords = (
  set: TimeAttackSet,
  rosterIds: string[]
) => {
  const records: SmsTimeAttackRecord[] = [];
  for (var i = 0; i < rosterIds.length; i++) {
    const rosterId = rosterIds[i];
    const record = createTimeAttackRecord(
      rosterId,
      rosterId,
      set.format.numTracks
    );
    set.tracks.forEach((track, trackIdx) => {
      const score = track.scores[rosterId];
      record.tracks[trackIdx] = {
        rank: undefined,
        sortedTimes: [],
        score,
      };
    });

    record.totalScore = sumTimeAttackRecordScore(record);
    if ((record.totalScore ?? 0) > 0) {
      records.push(record);
    }
  }

  assignTrackRanks(records, set.format.numTracks);
  if (records.length <= 1) {
    return records;
  }

  const sortedTeamRecords = records.sort(compareRecords);
  assignPositions(sortedTeamRecords);
  return sortedTeamRecords;
};

export const calculateDriverSmsTimeAttackRecords = (
  set: TimeAttackSet,
  teams: MatchTeams
) => {
  const records: SmsTimeAttackRecord[] = [];
  for (var i = 0; i < teams.rosters.length; i++) {
    const roster = teams.rosters[i];
    const rosterId = getRosterId(roster);
    if (!rosterId) {
      continue;
    }

    const rosterDriverIds = getRosterDriverIds(roster, teams.scope) ?? [];

    for (var j = 0; j < rosterDriverIds.length; j++) {
      const driverId = rosterDriverIds[j];
      const driverRecord = createTimeAttackRecord(
        driverId,
        rosterId,
        set.format.numTracks
      );
      set.tracks.forEach((track, trackIdx) => {
        const driverTimes = track.rounds.map((r) =>
          Object.keys(r.times).length > 0
            ? getAdjustedTime(
                r.times[driverId],
                Math.max(...Object.values(r.times)) + 22000
              )
            : undefined
        );

        if (driverTimes.some((t) => !!t)) {
          const score = getTimeAttackAverageScore(
            driverTimes,
            set.format.scoring as TimeAttackScoringAverage
          );

          driverRecord.tracks[trackIdx] = {
            rank: undefined,
            sortedTimes: [
              ...driverTimes.sort((a, b) => {
                if (a === b) {
                  return 0;
                }

                if (!a) {
                  return 1;
                }

                if (!b) {
                  return -1;
                }

                return a.time - b.time;
              }),
            ],
            score,
          };
        }
      });

      driverRecord.totalScore = sumTimeAttackRecordScore(driverRecord);
      if ((driverRecord.totalScore ?? 0) > 0) {
        records.push(driverRecord);
      }
    }
  }

  assignTrackRanks(records, set.format.numTracks);
  if (records.length <= 1) {
    return records;
  }

  const sortedDriverRecords = records.sort(compareRecords);
  assignPositions(sortedDriverRecords);
  return sortedDriverRecords;
};

const sumTimeAttackRecordScore = (record: SmsTimeAttackRecord) => {
  const scores = nonUndefined(record.tracks.map((t) => t?.score));
  if (scores.length === 0) {
    return undefined;
  }

  return scores.reduce((a, b) => a + b, 0);
};

const compareScore = (a: number | undefined, b: number | undefined): number => {
  if (a === undefined && b === undefined) {
    return 0;
  }

  if (b === undefined) {
    return -1;
  }

  if (a === undefined) {
    return 1;
  }

  return a - b;
};

const compareRecords = (
  a: SmsTimeAttackRecord,
  b: SmsTimeAttackRecord
): number => {
  return compareScore(a.totalScore, b.totalScore);
};

const assignPositions = (sortedRecords: SmsTimeAttackRecord[]) => {
  sortedRecords[0].position = 1;
  for (let i = 1; i < sortedRecords.length; i++) {
    if (compareRecords(sortedRecords[i - 1], sortedRecords[i]) === 0) {
      sortedRecords[i].position = sortedRecords[i - 1].position;
    } else {
      sortedRecords[i].position = i + 1;
    }
  }
};

const assignTrackRanks = (
  records: SmsTimeAttackRecord[],
  numTracks: number
) => {
  if (records.length === 0) {
    return;
  }

  for (let i = 0; i < numTracks; i++) {
    const sorted = [...records].sort((a, b) =>
      compareScore(a.tracks[i].score, b.tracks[i].score)
    );

    sorted[0].tracks[i].rank = 1;
    for (let j = 1; j < sorted.length; j++) {
      const current = sorted[j].tracks[i];
      const prior = sorted[j - 1].tracks[i];
      if (current.score === prior.score) {
        current.rank = prior.rank;
      } else {
        current.rank = j + 1;
      }
    }
  }
};
